import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { useCanAccess, useNotify, useTranslate } from "react-admin";

import { ResourceName } from "../../constants/resources";
import {
  type CustomerServiceAccountManagersQuery,
  SetupAccountPortalAccessDocument,
  type SetupAccountPortalAccessMutation,
  type SetupAccountPortalAccessMutationVariables,
} from "../../generated/graphql";
import { useOrganizationLearningCatalog } from "../../hooks/useOrganizationLearningCatalog";
import { client } from "../../lib/apolloClient";
import { CRM_COLORS } from "../../lib/crm";
import { OrganizationLearningGrantsContent } from "../../pages/OrganizationLearningGrantsPage";
import { dialogContentSx, dialogTitleSx } from "../../theme/dialogStyles";
import { OrganizationBillingManagement } from "../OrganizationBillingManagement";
import { CrmSection } from "./CrmSection";

type Props = {
  salesforceAccountId: string;
  onSetup: () => void;
  organization: CustomerServiceAccountManagersQuery["customerServiceAccountManagers"]["organization"];
};

const AccountPortalSetup = ({ salesforceAccountId, onSetup }: Pick<Props, "salesforceAccountId" | "onSetup">) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const setup = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    try {
      await client.mutate<SetupAccountPortalAccessMutation, SetupAccountPortalAccessMutationVariables>({
        mutation: SetupAccountPortalAccessDocument,
        variables: { salesforceAccountId },
      });
      setOpen(false);
      notify(translate("crm.accountManagers.setup.success"), { type: "success" });
      onSetup();
    } catch {
      notify(translate("crm.accountManagers.setup.error"), { type: "error" });
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };
  return (
    <Box sx={{ px: "16px", pt: "12px" }}>
      <Button variant="outlined" size="small" onClick={() => setOpen(true)} sx={{ textTransform: "none" }}>
        {translate("crm.accountManagers.setup.action")}
      </Button>
      {open && (
        <Dialog
          open
          onClose={() => {
            if (!saving) setOpen(false);
          }}
          fullWidth
          maxWidth="sm"
          aria-labelledby="account-portal-setup-title"
        >
          <DialogTitle id="account-portal-setup-title" sx={dialogTitleSx}>
            {translate("crm.accountManagers.setup.action")}
          </DialogTitle>
          <DialogContent sx={dialogContentSx}>
            <Typography variant="body2">{translate("crm.accountManagers.setup.confirm")}</Typography>
          </DialogContent>
          <DialogActions>
            <Button disabled={saving} onClick={() => setOpen(false)}>
              {translate("crm.accountManagers.dialog.cancel")}
            </Button>
            <Button disabled={saving} onClick={setup} variant="contained">
              {translate("crm.accountManagers.setup.action")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

const AccountLearningAccess = ({ organization }: { organization: NonNullable<Props["organization"]> }) => {
  const translate = useTranslate();
  const catalog = useOrganizationLearningCatalog(organization.id);
  const [managing, setManaging] = useState(false);
  const organizationName = organization.name || organization.id;

  return (
    <CrmSection
      id="crm-account-learning"
      testId="crm-account-learning-access"
      title={translate("crm.accountManagers.access.learning")}
    >
      <Box sx={{ px: "16px", py: "8px" }}>
        {catalog.loading ? (
          <CircularProgress size={20} aria-label={translate("learningGrants.loading")} />
        ) : catalog.error ? (
          <Alert
            severity="error"
            action={<Button onClick={() => void catalog.reload()}>{translate("learningGrants.actions.retry")}</Button>}
          >
            {translate("learningGrants.loadError")}
          </Alert>
        ) : (
          <Typography sx={{ fontSize: "14px", color: CRM_COLORS.text }} data-testid="crm-account-learning-summary">
            {catalog.isEmpty
              ? translate("learningGrants.emptyTitle")
              : translate("crm.accountManagers.access.learningSummary", {
                  courses: catalog.courses.filter(course => course.granted).length,
                  paths: catalog.learningPaths.filter(path => path.granted).length,
                })}
          </Typography>
        )}
        <Button
          size="small"
          variant="outlined"
          disabled={catalog.loading || Boolean(catalog.error) || catalog.isEmpty}
          onClick={() => setManaging(true)}
          sx={{ mt: "8px", textTransform: "none" }}
        >
          {translate("crm.accountManagers.access.manageLearning")}
        </Button>
      </Box>
      {managing && (
        <Dialog
          open
          fullWidth
          maxWidth="lg"
          onClose={() => setManaging(false)}
          aria-labelledby="crm-account-learning-grants-title"
        >
          <DialogTitle id="crm-account-learning-grants-title" sx={dialogTitleSx}>
            {translate("crm.accountManagers.access.learningDialog", { organization: organizationName })}
          </DialogTitle>
          <DialogContent sx={dialogContentSx}>
            <OrganizationLearningGrantsContent organizationName={organizationName} catalog={catalog} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManaging(false)} sx={{ textTransform: "none" }}>
              {translate("crm.accountManagers.access.close")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </CrmSection>
  );
};

export const AccountPortalAccess = ({ organization, salesforceAccountId, onSetup }: Props) => {
  const translate = useTranslate();
  const invitationPermission = useCanAccess({ action: "list", resource: ResourceName.Invitation });
  const billingPermission = useCanAccess({ action: "list", resource: ResourceName.OrganizationBilling });
  const learningPermission = useCanAccess({ action: "list", resource: ResourceName.LearningGrant });
  const canManageBilling = billingPermission.canAccess === true && !billingPermission.isPending;
  const canManageLearning = learningPermission.canAccess === true && !learningPermission.isPending;

  const canInvite = invitationPermission.canAccess === true && !invitationPermission.isPending;
  if (!canManageBilling && !canManageLearning && !canInvite) return null;
  if (!organization) {
    return <AccountPortalSetup key={salesforceAccountId} salesforceAccountId={salesforceAccountId} onSetup={onSetup} />;
  }

  return (
    <Box data-testid="crm-account-portal-access">
      {canManageBilling && (
        <CrmSection id="crm-account-billing" title={translate("crm.accountManagers.access.billing")}>
          <Box sx={{ px: "16px", py: "8px" }}>
            <OrganizationBillingManagement
              key={organization.id}
              organizationId={organization.id}
              organizationName={organization.name || organization.id}
              showAccountSummary
              onAccountChanged={() => undefined}
            />
          </Box>
        </CrmSection>
      )}
      {canManageLearning && <AccountLearningAccess key={organization.id} organization={organization} />}
    </Box>
  );
};
